/**
 * LandlordFilters — compact filter content rendered inside a Popover.
 * Does NOT include its own panel/wrapper; the parent provides that via <Popover>.
 */
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { Select } from '../ui/Select.js';
import { Button } from '../ui/Button.js';
import { areasApi } from '../../lib/api.js';
import type { LandlordSearchMode } from '@imza/shared';

export interface FilterState {
  q: string;
  mode: LandlordSearchMode;
  area_id: string;
  building: string;
  tag_id: string;
  stage: string;
  has_contact: string;
  needs_review: string;
  is_starred: string;
  assigned_to: string;
  sort: 'name' | 'recent' | 'units_desc';
  updated_since: string;
}

export const EMPTY_FILTERS: FilterState = {
  q: '',
  mode: 'smart',
  area_id: '',
  building: '',
  tag_id: '',
  stage: '',
  has_contact: '',
  needs_review: '',
  is_starred: '',
  assigned_to: '',
  sort: 'name',
  updated_since: '',
};

const STAGE_OPTIONS = [
  { value: 'new',          label: 'New' },
  { value: 'sale',         label: 'Sale' },
  { value: 'sold',         label: 'Sold' },
  { value: 'rent',         label: 'Rent' },
  { value: 'rented',       label: 'Rented' },
  { value: 'potential',    label: 'Potential' },
  { value: 'unavailable',  label: 'Unavailable' },
  { value: 'no_sale',      label: 'No Sale' },
  { value: 'no_answer',    label: 'No Answer' },
  { value: 'wrong_number', label: 'Wrong Number' },
  { value: 'agent',        label: 'Agent' },
];

const CONTACT_OPTIONS = [
  { value: 'true', label: 'Has contact' },
  { value: 'false', label: 'No contact' },
];

const SORT_OPTIONS = [
  { value: 'name', label: 'Name A–Z' },
  { value: 'recent', label: 'Recently updated' },
  { value: 'units_desc', label: 'Most units' },
];

const REVIEW_OPTIONS = [
  { value: 'true', label: 'Needs review' },
  { value: 'false', label: 'No flag' },
];

interface LandlordFiltersProps {
  filters: FilterState;
  onChange: (filters: FilterState) => void;
  onReset: () => void;
  resultCount?: number;
}

export function LandlordFilters({ filters, onChange, onReset, resultCount }: LandlordFiltersProps) {
  const areasQuery = useQuery({
    queryKey: ['areas'],
    queryFn: () => areasApi.list(),
    staleTime: 5 * 60 * 1000,
  });

  const areaOptions = areasQuery.data?.items.map((a) => ({
    value: a.id,
    label: a.name,
  })) ?? [];

  const hasActiveFilters =
    !!(filters.area_id || filters.building || filters.tag_id || filters.stage || filters.has_contact || filters.needs_review || filters.assigned_to);

  return (
    <div className="p-4 space-y-3">
      {/* Sort */}
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wide text-secondary mb-1.5">Sort</p>
        <Select
          options={SORT_OPTIONS}
          value={filters.sort}
          onChange={(e) => onChange({ ...filters, sort: e.target.value as FilterState['sort'] })}
          aria-label="Sort order"
          className="w-full text-sm"
        />
      </div>

      {/* Area */}
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wide text-secondary mb-1.5">Area</p>
        <Select
          placeholder="All areas"
          options={areaOptions as Array<{ value: string | number; label: string }>}
          value={filters.area_id}
          onChange={(e) => onChange({ ...filters, area_id: e.target.value })}
          aria-label="Filter by area"
          className="w-full text-sm"
        />
      </div>

      {/* Stage */}
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wide text-secondary mb-1.5">Stage</p>
        <Select
          placeholder="All stages"
          options={STAGE_OPTIONS}
          value={filters.stage}
          onChange={(e) => onChange({ ...filters, stage: e.target.value })}
          aria-label="Filter by stage"
          className="w-full text-sm"
        />
      </div>

      {/* Contact */}
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wide text-secondary mb-1.5">Contact info</p>
        <Select
          placeholder="Any"
          options={CONTACT_OPTIONS}
          value={filters.has_contact}
          onChange={(e) => onChange({ ...filters, has_contact: e.target.value })}
          aria-label="Filter by contact"
          className="w-full text-sm"
        />
      </div>

      {/* Needs review */}
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wide text-secondary mb-1.5">Review flag</p>
        <Select
          placeholder="Any"
          options={REVIEW_OPTIONS}
          value={filters.needs_review}
          onChange={(e) => onChange({ ...filters, needs_review: e.target.value })}
          aria-label="Filter by review flag"
          className="w-full text-sm"
        />
      </div>

      {/* Divider + footer */}
      <div className="pt-1 border-t border-border flex items-center justify-between">
        {resultCount !== undefined && (
          <p className="text-xs text-secondary">
            <span className="font-semibold text-primary-text">{resultCount.toLocaleString()}</span> results
          </p>
        )}
        {hasActiveFilters && (
          <button
            onClick={onReset}
            className="flex items-center gap-1 text-xs text-primary hover:text-blue-700 font-medium transition-colors ml-auto"
          >
            <X className="w-3 h-3" />
            Clear filters
          </button>
        )}
      </div>
    </div>
  );
}

/** Exported for use outside — count of active non-q filters */
export function countActiveFilters(f: FilterState): number {
  return [f.area_id, f.building, f.tag_id, f.stage, f.has_contact, f.needs_review, f.is_starred, f.assigned_to, f.updated_since].filter(Boolean).length;
}
